<template>
	<button
		type="button"
		:disabled="disabled"
		:aria-label="title"
		:aria-disabled="disabled ? 'true' : null"
		:data-testid="testId"
		class="portal-card"
		@click="handleClick"
	>
		<span class="portal-card__icon">
			<v-icon
				:size="28"
				color="primary"
			>
				{{ icon }}
			</v-icon>
		</span>

		<h3 class="portal-card__title">
			{{ title }}
		</h3>
		<p
			v-if="description"
			class="portal-card__description"
		>
			{{ description }}
		</p>

		<LucideIcon
			name="ArrowRight"
			:size="18"
			class="portal-card__arrow"
		/>
	</button>
</template>

<script>
import { LucideIcon } from '@syrenis/cassie-material-design'

/**
 * A product tile for a launcher page (AB#27211), the Vue twin of syrenis-ui's `PortalCard`. Keep the
 * two in step: 160px tall, 4px primary left accent, a 48px muted icon tile, 14px title over a 12px
 * description, and a hover lift.
 *
 * Deliberately not a variant of `overview-card.vue`: that component is used on eight other screens
 * whose look should not move with the launcher.
 */
export default {
	name: 'portal-card',
	components: {
		LucideIcon
	},
	props: {
		title: {
			type: String,
			required: true
		},
		description: {
			type: String,
			default: ''
		},
		/** Material Design Icons name, e.g. `mdi-cog-outline`. */
		icon: {
			type: String,
			default: ''
		},
		disabled: {
			type: Boolean,
			default: false
		},
		testId: {
			type: String,
			default: null
		}
	},
	methods: {
		handleClick () {
			if (!this.disabled) this.$emit('select')
		}
	}
}
</script>

<style lang="scss" scoped>
.portal-card {
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	width: 100%;
	min-height: 160px;
	padding: 20px;
	font-family: inherit;
	text-align: left;
	cursor: pointer;
	background-color: var(--card, #FFFFFF);
	border: 1px solid var(--border, #E0E2E5);
	border-left: 4px solid var(--primary, #406A94);
	border-radius: var(--radius, 8px);
	box-shadow: 0 1px 3px rgb(0 0 0 / 10%), 0 1px 2px rgb(0 0 0 / 6%);
	transition: box-shadow 200ms ease, transform 200ms ease;

	&:hover:not(:disabled) {
		box-shadow: 0 4px 12px rgb(0 0 0 / 10%);
		transform: translateY(-2px);
	}

	&:disabled {
		cursor: not-allowed;
		opacity: 0.5;
	}

	&:focus:not(:focus-visible) {
		outline: none;
	}

	&:focus-visible {
		outline: 2px solid var(--primary, #406A94);
		outline-offset: 2px;
	}

	@media (prefers-reduced-motion: reduce) {
		transition: none;

		&:hover:not(:disabled) {
			transform: none;
		}
	}

	&__icon {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 48px;
		height: 48px;
		margin-bottom: 16px;
		background-color: var(--muted, #F5F5F5);
		border-radius: var(--radius, 8px);
	}

	&__title {
		margin: 0;
		font-size: 0.875rem;
		font-weight: 600;
		line-height: 1.4;
		color: var(--foreground, #4A5568);
	}

	&__description {
		margin: 4px 0 0;
		font-size: 0.75rem;
		font-weight: 400;
		line-height: 1.5;
		color: var(--muted-foreground, #666E7A);
	}

	&__arrow {
		position: absolute;
		right: 16px;
		bottom: 16px;
		color: var(--muted-foreground, #9CA3AF);
	}
}
</style>
